import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { COMPANY_INFO, MENU_ITEMS } from '../data/scrapedData';

export function Header() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState({});
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setOpenDropdown(null);
        setMobileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const toggleMobileSubmenu = (title) => {
    setMobileExpanded(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const isRouteActive = (item) => {
    if (location.pathname === item.path) return true;
    if (item.subItems && item.subItems.some(sub => location.pathname === sub.path)) return true;
    return false;
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#1C1C1E]/10 shadow-xs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3885B8] rounded-md p-1" aria-label="Midtgaarden Forside">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=320,quality=100,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/den-selvejende-institution-midtgaarden-logo.png" 
              alt="Den Selvejende Institution Midtgaarden logo" 
              className="h-10 sm:h-12 w-auto object-contain shrink-0" 
            />
          </Link>

          {/* Desktop Navigation (Visible ONLY at >= 1200px) */}
          <nav ref={dropdownRef} className="hidden min-[1200px]:flex items-center gap-1 xl:gap-2">
            {MENU_ITEMS.map((item) => {
              const active = isRouteActive(item);
              const hasSub = item.subItems && item.subItems.length > 0;
              const isOpen = openDropdown === item.title;

              return (
                <div 
                  key={item.title} 
                  className="relative group"
                  onMouseEnter={() => hasSub && setOpenDropdown(item.title)}
                  onMouseLeave={() => hasSub && setOpenDropdown(null)}
                >
                  <div className="flex items-center">
                    {hasSub ? (
                      <Link
                        to={item.path}
                        className={`px-3 py-1.5 border-b-2 text-sm transition-all duration-150 flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3885B8] ${
                          active 
                            ? 'border-[#3885B8] text-[#1C1C1E] font-bold' 
                            : 'border-transparent text-[#1C1C1E] font-semibold hover:text-[#3885B8] hover:border-[#3885B8]/30'
                        }`}
                        aria-expanded={isOpen}
                      >
                        {item.title}
                        <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${active ? 'text-[#3885B8]' : 'text-[#1C1C1E]/50'}`} aria-hidden="true" />
                      </Link>
                    ) : (
                      <Link
                        to={item.path}
                        className={`px-3 py-1.5 border-b-2 text-sm transition-all duration-150 flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3885B8] ${
                          active 
                            ? 'border-[#3885B8] text-[#1C1C1E] font-bold' 
                            : 'border-transparent text-[#1C1C1E] font-semibold hover:text-[#3885B8] hover:border-[#3885B8]/30'
                        }`}
                      >
                        {item.title}
                      </Link>
                    )}
                  </div>

                  {/* Dropdown Menu */}
                  {hasSub && isOpen && (
                    <div className="absolute left-0 top-full pt-1.5 w-64 z-50">
                      <div className="bg-white rounded-xl shadow-xl border border-[#1C1C1E]/10 py-2 animate-in fade-in duration-150 overflow-hidden">
                        {item.path && (
                          <Link
                            to={item.path}
                            className={`block px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#3885B8] hover:bg-[#3885B8]/10 border-b border-[#1C1C1E]/5 transition-colors duration-150 ${
                              location.pathname === item.path ? 'bg-[#3885B8]/10' : ''
                            }`}
                          >
                            Oversigt over {item.title.toLowerCase()}
                          </Link>
                        )}
                        {item.subItems.map((sub) => {
                          const subActive = location.pathname === sub.path;
                          return (
                            <Link
                              key={sub.path}
                              to={sub.path}
                              className={`block px-4 py-2.5 text-sm transition-colors duration-150 border-l-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3885B8] ${
                                subActive 
                                  ? 'bg-[#1C1C1E]/10 text-[#1C1C1E] font-bold border-[#1C1C1E]' 
                                  : 'text-[#1C1C1E] hover:bg-[#1C1C1E]/5 border-transparent hover:text-[#1C1C1E]'
                              }`}
                            >
                              {sub.title}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Nav CTA Buttons for Telefon and Email */}
            <div className="flex items-center gap-2 ml-3">
              <a
                href={COMPANY_INFO.phoneTel}
                className="h-10 inline-flex items-center justify-center gap-2 px-4 rounded-full bg-[#3885B8] text-white text-sm font-semibold hover:bg-[#2A6E9B] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3885B8] whitespace-nowrap shadow-xs"
                aria-label="Ring til os på 75 17 20 80"
              >
                <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                <span className="whitespace-nowrap">75 17 20 80</span>
              </a>

              <a
                href={COMPANY_INFO.emailMailto}
                className="h-10 inline-flex items-center justify-center gap-2 px-4 rounded-full border-2 border-[#3885B8] text-[#3885B8] hover:bg-[#3885B8] hover:text-white text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3885B8] whitespace-nowrap shadow-xs"
                aria-label="Send e-mail til Midtgaarden"
              >
                <i className="fa-solid fa-envelope text-xs" aria-hidden="true" />
                <span className="whitespace-nowrap">Skriv til os</span>
              </a>
            </div>
          </nav>



          {/* Mobile Menu Button (Only visible < 1200px) */}
          <div className="flex items-center gap-2 min-[1200px]:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              type="button"
              className="inline-flex items-center justify-center p-2.5 rounded-full text-[#1C1C1E] hover:bg-[#1C1C1E]/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3885B8]"
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn menu"
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true" />
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Drawer (Visible < 1200px when toggled) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white border-b border-[#1C1C1E]/10 px-4 pt-3 pb-6 space-y-3 animate-in slide-in-from-top duration-200">
          
          <div className="space-y-1">
            {MENU_ITEMS.map((item) => {
              const active = isRouteActive(item);
              const hasSub = item.subItems && item.subItems.length > 0;
              const isExpanded = !!mobileExpanded[item.title];

              return (
                <div key={item.title} className="border-b border-[#1C1C1E]/5 last:border-none">
                  <div className="flex items-center justify-between py-1">
                    {hasSub ? (
                      <div className="flex items-center justify-between w-full">
                        <Link
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex-1 py-2.5 text-base font-medium rounded-l-full px-4 transition-colors ${
                            active ? 'bg-[#3885B8] text-white font-bold shadow-xs' : 'text-[#1C1C1E] hover:bg-[#1C1C1E]/5'
                          }`}
                        >
                          {item.title}
                        </Link>
                        <button
                          type="button"
                          onClick={() => toggleMobileSubmenu(item.title)}
                          aria-label={`Åbn undermenu for ${item.title}`}
                          className={`py-2.5 px-4 rounded-r-full transition-colors flex items-center justify-center ${
                            active ? 'bg-[#3885B8] text-white' : 'text-[#3885B8] hover:bg-[#1C1C1E]/5'
                          }`}
                        >
                          <i className={`fa-solid fa-chevron-down text-sm transition-transform ${isExpanded ? 'rotate-180' : ''}`} aria-hidden="true" />
                        </button>
                      </div>
                    ) : (
                      <Link
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block py-2.5 text-base font-medium rounded-full px-4 transition-colors ${
                          active ? 'bg-[#3885B8] text-white font-bold shadow-xs' : 'text-[#1C1C1E] hover:bg-[#1C1C1E]/5'
                        }`}
                      >
                        {item.title}
                      </Link>
                    )}
                  </div>

                  {hasSub && isExpanded && (
                    <div className="pl-4 pb-2 space-y-1 bg-[#F8FAFC] rounded-md my-1 py-2">
                      {item.subItems.map((sub) => {
                        const subActive = location.pathname === sub.path;
                        return (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`block py-2 px-3 text-sm rounded-md transition-colors ${
                              subActive ? 'text-[#1C1C1E] font-bold bg-[#1C1C1E]/15' : 'text-[#1C1C1E]/80 hover:bg-[#1C1C1E]/5'
                            }`}
                          >
                            {sub.title}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Mobile CTAs */}
          <div className="pt-4 flex flex-col sm:flex-row gap-3 border-t border-[#1C1C1E]/10">
            <a
              href={COMPANY_INFO.phoneTel}
              className="flex items-center justify-center gap-2 bg-[#3885B8] hover:bg-[#2A6E9B] text-white py-3 px-4 rounded-full font-semibold text-sm shadow-xs transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span className="whitespace-nowrap">Ring 75 17 20 80</span>
            </a>

            <a
              href={COMPANY_INFO.emailMailto}
              className="flex items-center justify-center gap-2 border-2 border-[#3885B8] text-[#3885B8] hover:bg-[#3885B8] hover:text-white py-3 px-4 rounded-full font-semibold text-sm transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope" aria-hidden="true" />
              <span className="whitespace-nowrap">Skriv til os</span>
            </a>
          </div>

        </div>
      )}
    </header>
  );
}
